/**
 * Hardware Abstraction Layer (HAL) - Centralized Hardware Dictionary
 * 
 * This file contains the definitive mappings of physical interface names
 * for all supported firewall vendors and models.
 * 
 * CRITICAL: These mappings must match the EXACT interface names as they
 * appear in each vendor's CLI. Incorrect names will cause deployment failures.
 * 
 * Sources:
 * - Fortinet: FortiOS CLI Reference, Hardware Guides
 * - Zyxel: USG FLEX / ATP CLI Reference Guide
 * - WatchGuard: Fireware CLI Reference
 * - pfSense: FreeBSD NIC Driver Documentation
 */

// ============================================
// Type Definitions
// ============================================

export interface HardwarePort {
  name: string;
  type: 'wan' | 'lan' | 'sfp' | 'mgmt' | 'ha' | 'dmz' | 'fortilink' | 'console' | 'usb' | 'combo';
  speed?: '1G' | '2.5G' | '10G' | '25G' | '40G' | '100G';
  isDefault?: boolean;
}

export interface HardwareModel {
  id: string;
  name: string;
  displayName: string;
  ports: HardwarePort[];
  defaultWanPort: string;
  defaultLanPort: string;
  defaultMgmtPort?: string;
  hasHardwareSwitch: boolean;
  hardwareSwitchName?: string;
  /** Ports that are part of the default hardware switch (must be removed before VLAN assignment) */
  hardwareSwitchPorts?: string[];
  /** Ports that have factory DHCP servers (must be disabled before custom config) - WatchGuard specific */
  factoryDhcpPorts?: string[];
  maxVlans: number;
  notes?: string;
}

export interface VendorDefinition {
  vendor: string;
  vendorCode: string;
  cliType: 'fortios' | 'zyxel_zld' | 'zyxel_uos' | 'watchguard' | 'pfsense';
  models: HardwareModel[];
}

// ============================================
// Fortinet FortiGate Models
// CRITICAL: Uses EXACT interface names from official documentation
// Hardware switches MUST be dismantled before VLAN assignment
// ============================================

const FORTINET_MODELS: HardwareModel[] = [
  // ---- 30G: Entry-Level Desktop ----
  // VERIFIED from real factory config: wan, lan1, lan2, a
  // Default hardware switch: "lan" groups lan1, lan2
  {
    id: 'fgt_30g',
    name: '30G',
    displayName: 'FortiGate 30G',
    ports: [
      { name: 'wan', type: 'wan', speed: '1G', isDefault: true },
      { name: 'lan1', type: 'lan', speed: '1G' },
      { name: 'lan2', type: 'lan', speed: '1G' },
      { name: 'a', type: 'fortilink', speed: '1G' },
    ],
    defaultWanPort: 'wan',
    defaultLanPort: 'lan1',
    hasHardwareSwitch: true,
    hardwareSwitchName: 'lan',
    hardwareSwitchPorts: ['lan1', 'lan2'],
    maxVlans: 255,
    notes: 'VERIFIED: Factory uses "lan" switch with lan1, lan2. DHCP server on lan interface.',
  },
  // ---- 40F: Entry-Level Desktop ----
  // Ports: wan, port1-port4, a, b, dmz
  // Default hardware switch: "internal" groups port1-port4
  {
    id: 'fgt_40f',
    name: '40F',
    displayName: 'FortiGate 40F',
    ports: [
      { name: 'wan', type: 'wan', speed: '1G', isDefault: true },
      { name: 'dmz', type: 'dmz', speed: '1G' },
      { name: 'port1', type: 'lan', speed: '1G' },
      { name: 'port2', type: 'lan', speed: '1G' },
      { name: 'port3', type: 'lan', speed: '1G' },
      { name: 'port4', type: 'lan', speed: '1G' },
      { name: 'a', type: 'fortilink', speed: '1G' },
      { name: 'b', type: 'fortilink', speed: '1G' },
    ],
    defaultWanPort: 'wan',
    defaultLanPort: 'port1',
    hasHardwareSwitch: true,
    hardwareSwitchName: 'internal',
    hardwareSwitchPorts: ['port1', 'port2', 'port3', 'port4'],
    maxVlans: 255,
    notes: 'Factory uses "internal" switch with port1-port4.',
  },
  // ---- 50G: Entry-Level Desktop with Dual WAN ----
  // Ports: wan1, wan2, port1-port4, a, b, dmz
  {
    id: 'fgt_50g',
    name: '50G',
    displayName: 'FortiGate 50G',
    ports: [
      { name: 'wan1', type: 'wan', speed: '1G', isDefault: true },
      { name: 'wan2', type: 'wan', speed: '1G' },
      { name: 'dmz', type: 'dmz', speed: '1G' },
      { name: 'port1', type: 'lan', speed: '1G' },
      { name: 'port2', type: 'lan', speed: '1G' },
      { name: 'port3', type: 'lan', speed: '1G' },
      { name: 'port4', type: 'lan', speed: '1G' },
      { name: 'a', type: 'fortilink', speed: '1G' },
      { name: 'b', type: 'fortilink', speed: '1G' },
    ],
    defaultWanPort: 'wan1',
    defaultLanPort: 'port1',
    hasHardwareSwitch: true,
    hardwareSwitchName: 'internal',
    hardwareSwitchPorts: ['port1', 'port2', 'port3', 'port4'],
    maxVlans: 255,
  },
  // ---- 60F: CRITICAL - Uses internal1-internal5, NOT portX ----
  // Ports: wan1, wan2, internal1-internal5, a, b, dmz
  // Default hardware switch: "internal" groups internal1-internal5
  {
    id: 'fgt_60f',
    name: '60F',
    displayName: 'FortiGate 60F',
    ports: [
      { name: 'wan1', type: 'wan', speed: '1G', isDefault: true },
      { name: 'wan2', type: 'wan', speed: '1G' },
      { name: 'dmz', type: 'dmz', speed: '1G' },
      { name: 'internal1', type: 'lan', speed: '1G' },
      { name: 'internal2', type: 'lan', speed: '1G' },
      { name: 'internal3', type: 'lan', speed: '1G' },
      { name: 'internal4', type: 'lan', speed: '1G' },
      { name: 'internal5', type: 'lan', speed: '1G' },
      { name: 'a', type: 'fortilink', speed: '1G' },
      { name: 'b', type: 'fortilink', speed: '1G' },
    ],
    defaultWanPort: 'wan1',
    defaultLanPort: 'internal1',
    hasHardwareSwitch: true,
    hardwareSwitchName: 'internal',
    hardwareSwitchPorts: ['internal1', 'internal2', 'internal3', 'internal4', 'internal5'],
    maxVlans: 255,
    notes: 'CRITICAL: Uses internalX naming, NOT portX. Factory "internal" switch.',
  },
  // ---- 70F: Mid-Range Desktop ----
  // Ports: wan1, wan2, port1-port4, a, b
  {
    id: 'fgt_70f',
    name: '70F',
    displayName: 'FortiGate 70F',
    ports: [
      { name: 'wan1', type: 'wan', speed: '1G', isDefault: true },
      { name: 'wan2', type: 'wan', speed: '1G' },
      { name: 'port1', type: 'lan', speed: '1G' },
      { name: 'port2', type: 'lan', speed: '1G' },
      { name: 'port3', type: 'lan', speed: '1G' },
      { name: 'port4', type: 'lan', speed: '1G' },
      { name: 'a', type: 'fortilink', speed: '1G' },
      { name: 'b', type: 'fortilink', speed: '1G' },
    ],
    defaultWanPort: 'wan1',
    defaultLanPort: 'port1',
    hasHardwareSwitch: true,
    hardwareSwitchName: 'internal',
    hardwareSwitchPorts: ['port1', 'port2', 'port3', 'port4'],
    maxVlans: 255,
  },
  // ---- 70G: Mid-Range Desktop with SFP+ ----
  // Ports: wan1, wan2, port1-port4, a, b, sfp1, sfp2
  {
    id: 'fgt_70g',
    name: '70G',
    displayName: 'FortiGate 70G',
    ports: [
      { name: 'wan1', type: 'wan', speed: '2.5G', isDefault: true },
      { name: 'wan2', type: 'wan', speed: '2.5G' },
      { name: 'port1', type: 'lan', speed: '1G' },
      { name: 'port2', type: 'lan', speed: '1G' },
      { name: 'port3', type: 'lan', speed: '1G' },
      { name: 'port4', type: 'lan', speed: '1G' },
      { name: 'a', type: 'fortilink', speed: '1G' },
      { name: 'b', type: 'fortilink', speed: '1G' },
      { name: 'sfp1', type: 'sfp', speed: '10G' },
      { name: 'sfp2', type: 'sfp', speed: '10G' },
    ],
    defaultWanPort: 'wan1',
    defaultLanPort: 'port1',
    hasHardwareSwitch: true,
    hardwareSwitchName: 'internal',
    hardwareSwitchPorts: ['port1', 'port2', 'port3', 'port4'],
    maxVlans: 255,
  },
  // ---- 80F: Mid-Range with 8 LAN ports ----
  // Ports: wan1, wan2, port1-port8, a, b, x1, x2
  {
    id: 'fgt_80f',
    name: '80F',
    displayName: 'FortiGate 80F',
    ports: [
      { name: 'wan1', type: 'wan', speed: '1G', isDefault: true },
      { name: 'wan2', type: 'wan', speed: '1G' },
      { name: 'port1', type: 'lan', speed: '1G' },
      { name: 'port2', type: 'lan', speed: '1G' },
      { name: 'port3', type: 'lan', speed: '1G' },
      { name: 'port4', type: 'lan', speed: '1G' },
      { name: 'port5', type: 'lan', speed: '1G' },
      { name: 'port6', type: 'lan', speed: '1G' },
      { name: 'port7', type: 'lan', speed: '1G' },
      { name: 'port8', type: 'lan', speed: '1G' },
      { name: 'a', type: 'fortilink', speed: '1G' },
      { name: 'b', type: 'fortilink', speed: '1G' },
      { name: 'x1', type: 'sfp', speed: '10G' },
      { name: 'x2', type: 'sfp', speed: '10G' },
    ],
    defaultWanPort: 'wan1',
    defaultLanPort: 'port1',
    hasHardwareSwitch: true,
    hardwareSwitchName: 'internal',
    hardwareSwitchPorts: ['port1', 'port2', 'port3', 'port4', 'port5', 'port6', 'port7', 'port8'],
    maxVlans: 255,
  },
  // ---- 90G: Mid-Range with 8 LAN ports and 2.5G WANs ----
  {
    id: 'fgt_90g',
    name: '90G',
    displayName: 'FortiGate 90G',
    ports: [
      { name: 'wan1', type: 'wan', speed: '2.5G', isDefault: true },
      { name: 'wan2', type: 'wan', speed: '2.5G' },
      { name: 'port1', type: 'lan', speed: '1G' },
      { name: 'port2', type: 'lan', speed: '1G' },
      { name: 'port3', type: 'lan', speed: '1G' },
      { name: 'port4', type: 'lan', speed: '1G' },
      { name: 'port5', type: 'lan', speed: '1G' },
      { name: 'port6', type: 'lan', speed: '1G' },
      { name: 'port7', type: 'lan', speed: '1G' },
      { name: 'port8', type: 'lan', speed: '1G' },
      { name: 'a', type: 'fortilink', speed: '1G' },
      { name: 'b', type: 'fortilink', speed: '1G' },
      { name: 'x1', type: 'sfp', speed: '10G' },
      { name: 'x2', type: 'sfp', speed: '10G' },
    ],
    defaultWanPort: 'wan1',
    defaultLanPort: 'port1',
    hasHardwareSwitch: true,
    hardwareSwitchName: 'internal',
    hardwareSwitchPorts: ['port1', 'port2', 'port3', 'port4', 'port5', 'port6', 'port7', 'port8'],
    maxVlans: 255,
  },
  // ---- 100F: Mid-Enterprise (NO hardware switch) ----
  // Ports: wan1, wan2, dmz, mgmt, ha, port1-port16, x1-x4
  {
    id: 'fgt_100f',
    name: '100F',
    displayName: 'FortiGate 100F',
    ports: [
      { name: 'wan1', type: 'wan', speed: '1G', isDefault: true },
      { name: 'wan2', type: 'wan', speed: '1G' },
      { name: 'dmz', type: 'dmz', speed: '1G' },
      { name: 'mgmt', type: 'mgmt', speed: '1G' },
      { name: 'ha', type: 'ha', speed: '1G' },
      { name: 'port1', type: 'lan', speed: '1G' },
      { name: 'port2', type: 'lan', speed: '1G' },
      { name: 'port3', type: 'lan', speed: '1G' },
      { name: 'port4', type: 'lan', speed: '1G' },
      { name: 'port5', type: 'lan', speed: '1G' },
      { name: 'port6', type: 'lan', speed: '1G' },
      { name: 'port7', type: 'lan', speed: '1G' },
      { name: 'port8', type: 'lan', speed: '1G' },
      { name: 'port9', type: 'lan', speed: '1G' },
      { name: 'port10', type: 'lan', speed: '1G' },
      { name: 'port11', type: 'lan', speed: '1G' },
      { name: 'port12', type: 'lan', speed: '1G' },
      { name: 'port13', type: 'lan', speed: '1G' },
      { name: 'port14', type: 'lan', speed: '1G' },
      { name: 'port15', type: 'lan', speed: '1G' },
      { name: 'port16', type: 'lan', speed: '1G' },
      { name: 'x1', type: 'sfp', speed: '10G' },
      { name: 'x2', type: 'sfp', speed: '10G' },
      { name: 'x3', type: 'sfp', speed: '10G' },
      { name: 'x4', type: 'sfp', speed: '10G' },
    ],
    defaultWanPort: 'wan1',
    defaultLanPort: 'port1',
    defaultMgmtPort: 'mgmt',
    hasHardwareSwitch: false,
    maxVlans: 4094,
    notes: 'Enterprise model - NO hardware switch. Direct port access.',
  },
  // ---- 120G: Mid-Enterprise 2.5G ----
  {
    id: 'fgt_120g',
    name: '120G',
    displayName: 'FortiGate 120G',
    ports: [
      { name: 'wan1', type: 'wan', speed: '2.5G', isDefault: true },
      { name: 'wan2', type: 'wan', speed: '2.5G' },
      { name: 'dmz', type: 'dmz', speed: '1G' },
      { name: 'mgmt', type: 'mgmt', speed: '1G' },
      { name: 'ha', type: 'ha', speed: '1G' },
      { name: 'port1', type: 'lan', speed: '2.5G' },
      { name: 'port2', type: 'lan', speed: '2.5G' },
      { name: 'port3', type: 'lan', speed: '2.5G' },
      { name: 'port4', type: 'lan', speed: '2.5G' },
      { name: 'port5', type: 'lan', speed: '2.5G' },
      { name: 'port6', type: 'lan', speed: '2.5G' },
      { name: 'port7', type: 'lan', speed: '2.5G' },
      { name: 'port8', type: 'lan', speed: '2.5G' },
      { name: 'port9', type: 'lan', speed: '2.5G' },
      { name: 'port10', type: 'lan', speed: '2.5G' },
      { name: 'port11', type: 'lan', speed: '2.5G' },
      { name: 'port12', type: 'lan', speed: '2.5G' },
      { name: 'port13', type: 'lan', speed: '2.5G' },
      { name: 'port14', type: 'lan', speed: '2.5G' },
      { name: 'port15', type: 'lan', speed: '2.5G' },
      { name: 'port16', type: 'lan', speed: '2.5G' },
      { name: 'x1', type: 'sfp', speed: '10G' },
      { name: 'x2', type: 'sfp', speed: '10G' },
      { name: 'x3', type: 'sfp', speed: '10G' },
      { name: 'x4', type: 'sfp', speed: '10G' },
    ],
    defaultWanPort: 'wan1',
    defaultLanPort: 'port1',
    defaultMgmtPort: 'mgmt',
    hasHardwareSwitch: false,
    maxVlans: 4094,
  },
  // ---- 200F: Enterprise ----
  {
    id: 'fgt_200f',
    name: '200F',
    displayName: 'FortiGate 200F',
    ports: [
      { name: 'wan1', type: 'wan', speed: '1G', isDefault: true },
      { name: 'wan2', type: 'wan', speed: '1G' },
      { name: 'dmz', type: 'dmz', speed: '1G' },
      { name: 'mgmt', type: 'mgmt', speed: '1G' },
      { name: 'ha', type: 'ha', speed: '1G' },
      { name: 'port1', type: 'lan', speed: '1G' },
      { name: 'port2', type: 'lan', speed: '1G' },
      { name: 'port3', type: 'lan', speed: '1G' },
      { name: 'port4', type: 'lan', speed: '1G' },
      { name: 'port5', type: 'lan', speed: '1G' },
      { name: 'port6', type: 'lan', speed: '1G' },
      { name: 'port7', type: 'lan', speed: '1G' },
      { name: 'port8', type: 'lan', speed: '1G' },
      { name: 'port9', type: 'lan', speed: '1G' },
      { name: 'port10', type: 'lan', speed: '1G' },
      { name: 'port11', type: 'lan', speed: '1G' },
      { name: 'port12', type: 'lan', speed: '1G' },
      { name: 'port13', type: 'lan', speed: '1G' },
      { name: 'port14', type: 'lan', speed: '1G' },
      { name: 'port15', type: 'lan', speed: '1G' },
      { name: 'port16', type: 'lan', speed: '1G' },
      { name: 'x1', type: 'sfp', speed: '10G' },
      { name: 'x2', type: 'sfp', speed: '10G' },
      { name: 'x3', type: 'sfp', speed: '10G' },
      { name: 'x4', type: 'sfp', speed: '10G' },
    ],
    defaultWanPort: 'wan1',
    defaultLanPort: 'port1',
    defaultMgmtPort: 'mgmt',
    hasHardwareSwitch: false,
    maxVlans: 4094,
  },
  // ---- VM: Virtual Machine ----
  {
    id: 'fgt_vm',
    name: 'FortiGate-VM',
    displayName: 'FortiGate VM',
    ports: [
      { name: 'port1', type: 'wan', speed: '10G', isDefault: true },
      { name: 'port2', type: 'lan', speed: '10G' },
      { name: 'port3', type: 'lan', speed: '10G' },
      { name: 'port4', type: 'lan', speed: '10G' },
      { name: 'port5', type: 'lan', speed: '10G' },
      { name: 'port6', type: 'lan', speed: '10G' },
      { name: 'port7', type: 'lan', speed: '10G' },
      { name: 'port8', type: 'lan', speed: '10G' },
      { name: 'port9', type: 'lan', speed: '10G' },
      { name: 'port10', type: 'lan', speed: '10G' },
    ],
    defaultWanPort: 'port1',
    defaultLanPort: 'port2',
    hasHardwareSwitch: false,
    maxVlans: 4094,
    notes: 'Virtual appliance. Port count depends on hypervisor vNICs.',
  },
];

// ============================================
// Zyxel USG FLEX / ATP Models (ZLD Firmware)
// CRITICAL: FLEX 50 uses wan/lan1/lan2/dmz/opt naming
// Other models use geX naming
// ============================================

const ZYXEL_MODELS: HardwareModel[] = [
  // ---- FLEX 50: Entry-Level with explicit naming ----
  {
    id: 'zyxel_usg_flex_50',
    name: 'USG FLEX 50',
    displayName: 'Zyxel USG FLEX 50',
    ports: [
      { name: 'wan', type: 'wan', speed: '1G', isDefault: true },
      { name: 'lan1', type: 'lan', speed: '1G' },
      { name: 'lan2', type: 'lan', speed: '1G' },
      { name: 'dmz', type: 'dmz', speed: '1G' },
      { name: 'opt', type: 'lan', speed: '1G' },
    ],
    defaultWanPort: 'wan',
    defaultLanPort: 'lan1',
    hasHardwareSwitch: false,
    maxVlans: 64,
    notes: 'CRITICAL: Uses wan/lan1/lan2/dmz/opt, NOT geX.',
  },
  // ---- FLEX 100 / ATP100: geX naming ----
  {
    id: 'zyxel_usg_flex_100',
    name: 'USG FLEX 100',
    displayName: 'Zyxel USG FLEX 100',
    ports: [
      { name: 'ge1', type: 'wan', speed: '1G', isDefault: true },
      { name: 'ge2', type: 'wan', speed: '1G' },
      { name: 'ge3', type: 'lan', speed: '1G' },
      { name: 'ge4', type: 'lan', speed: '1G' },
      { name: 'ge5', type: 'lan', speed: '1G' },
      { name: 'ge6', type: 'lan', speed: '1G' },
    ],
    defaultWanPort: 'ge1',
    defaultLanPort: 'ge3',
    hasHardwareSwitch: false,
    maxVlans: 64,
  },
  {
    id: 'zyxel_atp_100',
    name: 'ATP100',
    displayName: 'Zyxel ATP100',
    ports: [
      { name: 'ge1', type: 'wan', speed: '1G', isDefault: true },
      { name: 'ge2', type: 'wan', speed: '1G' },
      { name: 'ge3', type: 'lan', speed: '1G' },
      { name: 'ge4', type: 'lan', speed: '1G' },
      { name: 'ge5', type: 'lan', speed: '1G' },
      { name: 'ge6', type: 'lan', speed: '1G' },
    ],
    defaultWanPort: 'ge1',
    defaultLanPort: 'ge3',
    hasHardwareSwitch: false,
    maxVlans: 64,
  },
  // ---- FLEX 200 ----
  {
    id: 'zyxel_usg_flex_200',
    name: 'USG FLEX 200',
    displayName: 'Zyxel USG FLEX 200',
    ports: [
      { name: 'ge1', type: 'wan', speed: '1G', isDefault: true },
      { name: 'ge2', type: 'wan', speed: '1G' },
      { name: 'ge3', type: 'lan', speed: '1G' },
      { name: 'ge4', type: 'lan', speed: '1G' },
      { name: 'ge5', type: 'lan', speed: '1G' },
      { name: 'ge6', type: 'lan', speed: '1G' },
      { name: 'ge7', type: 'lan', speed: '1G' },
    ],
    defaultWanPort: 'ge1',
    defaultLanPort: 'ge3',
    hasHardwareSwitch: false,
    maxVlans: 64,
  },
  // ---- FLEX 500 / ATP500 ----
  {
    id: 'zyxel_usg_flex_500',
    name: 'USG FLEX 500',
    displayName: 'Zyxel USG FLEX 500',
    ports: [
      { name: 'ge1', type: 'wan', speed: '1G', isDefault: true },
      { name: 'ge2', type: 'wan', speed: '1G' },
      { name: 'ge3', type: 'lan', speed: '1G' },
      { name: 'ge4', type: 'lan', speed: '1G' },
      { name: 'ge5', type: 'lan', speed: '1G' },
      { name: 'ge6', type: 'lan', speed: '1G' },
      { name: 'ge7', type: 'lan', speed: '1G' },
      { name: 'ge8', type: 'lan', speed: '1G' },
      { name: 'geX', type: 'sfp', speed: '10G' },
    ],
    defaultWanPort: 'ge1',
    defaultLanPort: 'ge3',
    hasHardwareSwitch: false,
    maxVlans: 128,
  },
  {
    id: 'zyxel_atp_500',
    name: 'ATP500',
    displayName: 'Zyxel ATP500',
    ports: [
      { name: 'ge1', type: 'wan', speed: '1G', isDefault: true },
      { name: 'ge2', type: 'wan', speed: '1G' },
      { name: 'ge3', type: 'lan', speed: '1G' },
      { name: 'ge4', type: 'lan', speed: '1G' },
      { name: 'ge5', type: 'lan', speed: '1G' },
      { name: 'ge6', type: 'lan', speed: '1G' },
      { name: 'ge7', type: 'lan', speed: '1G' },
      { name: 'ge8', type: 'lan', speed: '1G' },
      { name: 'geX', type: 'sfp', speed: '10G' },
    ],
    defaultWanPort: 'ge1',
    defaultLanPort: 'ge3',
    hasHardwareSwitch: false,
    maxVlans: 128,
  },
  // ---- FLEX 700 / ATP800 ----
  {
    id: 'zyxel_usg_flex_700',
    name: 'USG FLEX 700',
    displayName: 'Zyxel USG FLEX 700',
    ports: [
      { name: 'ge1', type: 'wan', speed: '1G', isDefault: true },
      { name: 'ge2', type: 'wan', speed: '1G' },
      { name: 'ge3', type: 'lan', speed: '1G' },
      { name: 'ge4', type: 'lan', speed: '1G' },
      { name: 'ge5', type: 'lan', speed: '1G' },
      { name: 'ge6', type: 'lan', speed: '1G' },
      { name: 'ge7', type: 'lan', speed: '1G' },
      { name: 'ge8', type: 'lan', speed: '1G' },
      { name: 'ge9', type: 'lan', speed: '1G' },
      { name: 'ge10', type: 'lan', speed: '1G' },
      { name: 'ge11', type: 'lan', speed: '1G' },
      { name: 'ge12', type: 'lan', speed: '1G' },
      { name: 'geX', type: 'sfp', speed: '10G' },
    ],
    defaultWanPort: 'ge1',
    defaultLanPort: 'ge3',
    hasHardwareSwitch: false,
    maxVlans: 128,
  },
  {
    id: 'zyxel_atp_800',
    name: 'ATP800',
    displayName: 'Zyxel ATP800',
    ports: [
      { name: 'ge1', type: 'wan', speed: '1G', isDefault: true },
      { name: 'ge2', type: 'wan', speed: '1G' },
      { name: 'ge3', type: 'lan', speed: '1G' },
      { name: 'ge4', type: 'lan', speed: '1G' },
      { name: 'ge5', type: 'lan', speed: '1G' },
      { name: 'ge6', type: 'lan', speed: '1G' },
      { name: 'ge7', type: 'lan', speed: '1G' },
      { name: 'ge8', type: 'lan', speed: '1G' },
      { name: 'ge9', type: 'lan', speed: '1G' },
      { name: 'ge10', type: 'lan', speed: '1G' },
      { name: 'ge11', type: 'lan', speed: '1G' },
      { name: 'ge12', type: 'lan', speed: '1G' },
      { name: 'geX', type: 'sfp', speed: '10G' },
    ],
    defaultWanPort: 'ge1',
    defaultLanPort: 'ge3',
    hasHardwareSwitch: false,
    maxVlans: 128,
  },
];

// ============================================
// Zyxel Nebula (uOS) Models
// CRITICAL: Interface names limited to 11 characters
// ============================================

const ZYXEL_UOS_MODELS: HardwareModel[] = [
  // ---- USG LITE 60AX: Uses wan/lanX naming ----
  {
    id: 'zyxel_usg_lite_60ax',
    name: 'USG LITE 60AX',
    displayName: 'Zyxel USG LITE 60AX',
    ports: [
      { name: 'wan', type: 'wan', speed: '1G', isDefault: true },
      { name: 'lan1', type: 'lan', speed: '1G' },
      { name: 'lan2', type: 'lan', speed: '1G' },
      { name: 'lan3', type: 'lan', speed: '1G' },
      { name: 'lan4', type: 'lan', speed: '1G' },
    ],
    defaultWanPort: 'wan',
    defaultLanPort: 'lan1',
    hasHardwareSwitch: false,
    maxVlans: 32,
    notes: 'Nebula cloud-managed. Uses wan/lanX naming.',
  },
  // ---- FLEX 100AX ----
  {
    id: 'zyxel_usg_flex_100ax',
    name: 'USG FLEX 100AX',
    displayName: 'Zyxel USG FLEX 100AX',
    ports: [
      { name: 'wan', type: 'wan', speed: '1G', isDefault: true },
      { name: 'wan2', type: 'wan', speed: '1G' },
      { name: 'lan1', type: 'lan', speed: '1G' },
      { name: 'lan2', type: 'lan', speed: '1G' },
      { name: 'lan3', type: 'lan', speed: '1G' },
      { name: 'lan4', type: 'lan', speed: '1G' },
    ],
    defaultWanPort: 'wan',
    defaultLanPort: 'lan1',
    hasHardwareSwitch: false,
    maxVlans: 32,
  },
  // ---- FLEX 200H (uOS with 2.5G) ----
  {
    id: 'zyxel_usg_flex_200h',
    name: 'USG FLEX 200H',
    displayName: 'Zyxel USG FLEX 200H',
    ports: [
      { name: 'ge1', type: 'wan', speed: '2.5G', isDefault: true },
      { name: 'ge2', type: 'wan', speed: '2.5G' },
      { name: 'ge3', type: 'lan', speed: '2.5G' },
      { name: 'ge4', type: 'lan', speed: '2.5G' },
      { name: 'ge5', type: 'lan', speed: '2.5G' },
      { name: 'ge6', type: 'lan', speed: '2.5G' },
      { name: 'ge7', type: 'lan', speed: '2.5G' },
      { name: 'ge8', type: 'lan', speed: '2.5G' },
    ],
    defaultWanPort: 'ge1',
    defaultLanPort: 'ge3',
    hasHardwareSwitch: false,
    maxVlans: 64,
    notes: 'H-series with 2.5G ports.',
  },
  // ---- FLEX 500H (uOS) ----
  {
    id: 'zyxel_usg_flex_500h',
    name: 'USG FLEX 500H',
    displayName: 'Zyxel USG FLEX 500H',
    ports: [
      { name: 'ge1', type: 'wan', speed: '2.5G', isDefault: true },
      { name: 'ge2', type: 'wan', speed: '2.5G' },
      { name: 'ge3', type: 'lan', speed: '2.5G' },
      { name: 'ge4', type: 'lan', speed: '2.5G' },
      { name: 'ge5', type: 'lan', speed: '2.5G' },
      { name: 'ge6', type: 'lan', speed: '2.5G' },
      { name: 'ge7', type: 'lan', speed: '2.5G' },
      { name: 'ge8', type: 'lan', speed: '2.5G' },
      { name: 'ge9', type: 'lan', speed: '2.5G' },
      { name: 'ge10', type: 'lan', speed: '2.5G' },
      { name: 'ge11', type: 'lan', speed: '2.5G' },
      { name: 'ge12', type: 'lan', speed: '2.5G' },
      { name: 'geX', type: 'sfp', speed: '10G' },
    ],
    defaultWanPort: 'ge1',
    defaultLanPort: 'ge3',
    hasHardwareSwitch: false,
    maxVlans: 128,
    notes: 'Requires "vrf main" context in CLI.',
  },
];

// ============================================
// WatchGuard Firebox Models
// CRITICAL: Uses eth0, eth1, eth2 naming
// Factory: eth0=DHCP, eth1=10.0.1.1/24 with DHCP server
// ============================================

const WATCHGUARD_MODELS: HardwareModel[] = [
  // ---- T25 / T45: 5 ports ----
  {
    id: 'wg_t25',
    name: 'T25',
    displayName: 'WatchGuard Firebox T25',
    ports: [
      { name: 'eth0', type: 'wan', speed: '1G', isDefault: true },
      { name: 'eth1', type: 'lan', speed: '1G' },
      { name: 'eth2', type: 'lan', speed: '1G' },
      { name: 'eth3', type: 'lan', speed: '1G' },
      { name: 'eth4', type: 'lan', speed: '1G' },
    ],
    defaultWanPort: 'eth0',
    defaultLanPort: 'eth1',
    hasHardwareSwitch: false,
    factoryDhcpPorts: ['eth1', 'eth2', 'eth3', 'eth4'],
    maxVlans: 256,
    notes: 'Factory: eth0=External DHCP, eth1-eth4=10.0.x.1/24 with DHCP servers.',
  },
  {
    id: 'wg_t45',
    name: 'T45',
    displayName: 'WatchGuard Firebox T45',
    ports: [
      { name: 'eth0', type: 'wan', speed: '1G', isDefault: true },
      { name: 'eth1', type: 'lan', speed: '1G' },
      { name: 'eth2', type: 'lan', speed: '1G' },
      { name: 'eth3', type: 'lan', speed: '1G' },
      { name: 'eth4', type: 'lan', speed: '1G' },
    ],
    defaultWanPort: 'eth0',
    defaultLanPort: 'eth1',
    hasHardwareSwitch: false,
    factoryDhcpPorts: ['eth1', 'eth2', 'eth3', 'eth4'],
    maxVlans: 256,
  },
  // ---- T85 / M290 / M390: 8 ports ----
  {
    id: 'wg_t85',
    name: 'T85',
    displayName: 'WatchGuard Firebox T85',
    ports: [
      { name: 'eth0', type: 'wan', speed: '1G', isDefault: true },
      { name: 'eth1', type: 'lan', speed: '1G' },
      { name: 'eth2', type: 'lan', speed: '1G' },
      { name: 'eth3', type: 'lan', speed: '1G' },
      { name: 'eth4', type: 'lan', speed: '1G' },
      { name: 'eth5', type: 'lan', speed: '1G' },
      { name: 'eth6', type: 'lan', speed: '1G' },
      { name: 'eth7', type: 'lan', speed: '1G' },
    ],
    defaultWanPort: 'eth0',
    defaultLanPort: 'eth1',
    hasHardwareSwitch: false,
    factoryDhcpPorts: ['eth1', 'eth2', 'eth3', 'eth4', 'eth5', 'eth6', 'eth7'],
    maxVlans: 256,
  },
  {
    id: 'wg_m290',
    name: 'M290',
    displayName: 'WatchGuard Firebox M290',
    ports: [
      { name: 'eth0', type: 'wan', speed: '1G', isDefault: true },
      { name: 'eth1', type: 'lan', speed: '1G' },
      { name: 'eth2', type: 'lan', speed: '1G' },
      { name: 'eth3', type: 'lan', speed: '1G' },
      { name: 'eth4', type: 'lan', speed: '1G' },
      { name: 'eth5', type: 'lan', speed: '1G' },
      { name: 'eth6', type: 'lan', speed: '1G' },
      { name: 'eth7', type: 'lan', speed: '1G' },
    ],
    defaultWanPort: 'eth0',
    defaultLanPort: 'eth1',
    hasHardwareSwitch: false,
    factoryDhcpPorts: ['eth1', 'eth2', 'eth3', 'eth4', 'eth5', 'eth6', 'eth7'],
    maxVlans: 512,
  },
  {
    id: 'wg_m390',
    name: 'M390',
    displayName: 'WatchGuard Firebox M390',
    ports: [
      { name: 'eth0', type: 'wan', speed: '1G', isDefault: true },
      { name: 'eth1', type: 'lan', speed: '1G' },
      { name: 'eth2', type: 'lan', speed: '1G' },
      { name: 'eth3', type: 'lan', speed: '1G' },
      { name: 'eth4', type: 'lan', speed: '1G' },
      { name: 'eth5', type: 'lan', speed: '1G' },
      { name: 'eth6', type: 'lan', speed: '1G' },
      { name: 'eth7', type: 'lan', speed: '1G' },
    ],
    defaultWanPort: 'eth0',
    defaultLanPort: 'eth1',
    hasHardwareSwitch: false,
    factoryDhcpPorts: ['eth1', 'eth2', 'eth3', 'eth4', 'eth5', 'eth6', 'eth7'],
    maxVlans: 512,
  },
  // ---- M590: 10 ports ----
  {
    id: 'wg_m590',
    name: 'M590',
    displayName: 'WatchGuard Firebox M590',
    ports: [
      { name: 'eth0', type: 'wan', speed: '1G', isDefault: true },
      { name: 'eth1', type: 'lan', speed: '1G' },
      { name: 'eth2', type: 'lan', speed: '1G' },
      { name: 'eth3', type: 'lan', speed: '1G' },
      { name: 'eth4', type: 'lan', speed: '1G' },
      { name: 'eth5', type: 'lan', speed: '1G' },
      { name: 'eth6', type: 'lan', speed: '1G' },
      { name: 'eth7', type: 'lan', speed: '1G' },
      { name: 'eth8', type: 'lan', speed: '1G' },
      { name: 'eth9', type: 'lan', speed: '1G' },
    ],
    defaultWanPort: 'eth0',
    defaultLanPort: 'eth1',
    hasHardwareSwitch: false,
    factoryDhcpPorts: ['eth1', 'eth2', 'eth3', 'eth4', 'eth5', 'eth6', 'eth7', 'eth8', 'eth9'],
    maxVlans: 1024,
  },
];

// ============================================
// pfSense / Netgate Models
// CRITICAL: Uses FreeBSD kernel driver names
// Netgate 1100: MUST use tagged VLANs on mvneta0
// ============================================

const PFSENSE_MODELS: HardwareModel[] = [
  // ---- Netgate 1100: VLAN-based interfaces ----
  {
    id: 'pf_netgate_1100',
    name: 'Netgate 1100',
    displayName: 'Netgate 1100',
    ports: [
      { name: 'mvneta0.4090', type: 'wan', speed: '1G', isDefault: true },
      { name: 'mvneta0.4091', type: 'lan', speed: '1G' },
      { name: 'mvneta0.4092', type: 'lan', speed: '1G' },
    ],
    defaultWanPort: 'mvneta0.4090',
    defaultLanPort: 'mvneta0.4091',
    hasHardwareSwitch: false,
    maxVlans: 4094,
    notes: 'CRITICAL: Do NOT use mvneta0 directly. Must use hardcoded VLANs: .4090=WAN, .4091=LAN, .4092=OPT.',
  },
  // ---- Netgate 2100: mvneta driver ----
  {
    id: 'pf_netgate_2100',
    name: 'Netgate 2100',
    displayName: 'Netgate 2100',
    ports: [
      { name: 'mvneta0', type: 'wan', speed: '1G', isDefault: true },
      { name: 'mvneta1', type: 'lan', speed: '1G' },
    ],
    defaultWanPort: 'mvneta0',
    defaultLanPort: 'mvneta1',
    hasHardwareSwitch: true,
    hardwareSwitchName: 'mvneta1',
    maxVlans: 4094,
    notes: 'mvneta1 is a LAN switch with 4 ports internally.',
  },
  // ---- Netgate 4100: Intel ix + igc drivers ----
  {
    id: 'pf_netgate_4100',
    name: 'Netgate 4100',
    displayName: 'Netgate 4100',
    ports: [
      { name: 'ix3', type: 'wan', speed: '10G', isDefault: true },
      { name: 'ix2', type: 'wan', speed: '10G' },
      { name: 'igc0', type: 'lan', speed: '2.5G' },
      { name: 'igc1', type: 'lan', speed: '2.5G' },
      { name: 'igc2', type: 'lan', speed: '2.5G' },
      { name: 'igc3', type: 'lan', speed: '2.5G' },
    ],
    defaultWanPort: 'ix3',
    defaultLanPort: 'igc0',
    hasHardwareSwitch: false,
    maxVlans: 4094,
    notes: 'ix=Intel 10G SFP+, igc=Intel 2.5G RJ45.',
  },
  // ---- Netgate 6100: 4x 10G SFP+ WANs ----
  {
    id: 'pf_netgate_6100',
    name: 'Netgate 6100',
    displayName: 'Netgate 6100',
    ports: [
      { name: 'ix3', type: 'wan', speed: '10G', isDefault: true },
      { name: 'ix2', type: 'wan', speed: '10G' },
      { name: 'ix0', type: 'wan', speed: '10G' },
      { name: 'ix1', type: 'wan', speed: '10G' },
      { name: 'igc0', type: 'lan', speed: '2.5G' },
      { name: 'igc1', type: 'lan', speed: '2.5G' },
      { name: 'igc2', type: 'lan', speed: '2.5G' },
      { name: 'igc3', type: 'lan', speed: '2.5G' },
    ],
    defaultWanPort: 'ix3',
    defaultLanPort: 'igc0',
    hasHardwareSwitch: false,
    maxVlans: 4094,
    notes: 'ix0-ix3=Intel 10G SFP+, igc0-igc3=Intel 2.5G RJ45.',
  },
  // ---- Generic VM: VirtIO ----
  {
    id: 'pf_generic_vm',
    name: 'pfSense-VM',
    displayName: 'pfSense VM (VirtIO)',
    ports: [
      { name: 'vtnet0', type: 'wan', speed: '10G', isDefault: true },
      { name: 'vtnet1', type: 'lan', speed: '10G' },
      { name: 'vtnet2', type: 'lan', speed: '10G' },
      { name: 'vtnet3', type: 'lan', speed: '10G' },
    ],
    defaultWanPort: 'vtnet0',
    defaultLanPort: 'vtnet1',
    hasHardwareSwitch: false,
    maxVlans: 4094,
    notes: 'VirtIO NICs. Adjust for hypervisor.',
  },
  // ---- Generic Intel em driver ----
  {
    id: 'pf_generic_em',
    name: 'pfSense-EM',
    displayName: 'pfSense Generic (em NICs)',
    ports: [
      { name: 'em0', type: 'wan', speed: '1G', isDefault: true },
      { name: 'em1', type: 'lan', speed: '1G' },
      { name: 'em2', type: 'lan', speed: '1G' },
      { name: 'em3', type: 'lan', speed: '1G' },
    ],
    defaultWanPort: 'em0',
    defaultLanPort: 'em1',
    hasHardwareSwitch: false,
    maxVlans: 4094,
    notes: 'Intel em driver NICs.',
  },
  // ---- Generic Intel igb driver ----
  {
    id: 'pf_generic_igb',
    name: 'pfSense-IGB',
    displayName: 'pfSense Generic (igb NICs)',
    ports: [
      { name: 'igb0', type: 'wan', speed: '1G', isDefault: true },
      { name: 'igb1', type: 'lan', speed: '1G' },
      { name: 'igb2', type: 'lan', speed: '1G' },
      { name: 'igb3', type: 'lan', speed: '1G' },
    ],
    defaultWanPort: 'igb0',
    defaultLanPort: 'igb1',
    hasHardwareSwitch: false,
    maxVlans: 4094,
    notes: 'Intel igb driver NICs.',
  },
];

// ============================================
// Master Hardware Dictionary
// ============================================

export const HARDWARE_VENDORS: VendorDefinition[] = [
  {
    vendor: 'Fortinet',
    vendorCode: 'fortinet',
    cliType: 'fortios',
    models: FORTINET_MODELS,
  },
  {
    vendor: 'Zyxel',
    vendorCode: 'zyxel',
    cliType: 'zyxel_zld',
    models: ZYXEL_MODELS,
  },
  {
    vendor: 'Zyxel_uOS',
    vendorCode: 'zyxel_uos',
    cliType: 'zyxel_uos',
    models: ZYXEL_UOS_MODELS,
  },
  {
    vendor: 'WatchGuard',
    vendorCode: 'watchguard',
    cliType: 'watchguard',
    models: WATCHGUARD_MODELS,
  },
  {
    vendor: 'pfSense',
    vendorCode: 'pfsense',
    cliType: 'pfsense',
    models: PFSENSE_MODELS,
  },
];

// ============================================
// Helper Functions
// ============================================

/**
 * Get all models for a specific vendor
 */
export function getModelsByVendor(vendorCode: string): HardwareModel[] {
  const vendor = HARDWARE_VENDORS.find(v => v.vendorCode === vendorCode || v.vendor === vendorCode);
  return vendor?.models || [];
}

/**
 * Get a specific model by its name or ID
 */
export function getModelByName(modelName: string): HardwareModel | undefined {
  for (const vendor of HARDWARE_VENDORS) {
    const model = vendor.models.find(m => m.name === modelName || m.id === modelName || m.displayName === modelName);
    if (model) return model;
  }
  return undefined;
}

/**
 * Get the vendor for a specific model
 */
export function getVendorForModel(modelName: string): VendorDefinition | undefined {
  for (const vendor of HARDWARE_VENDORS) {
    const model = vendor.models.find(m => m.name === modelName || m.id === modelName);
    if (model) return vendor;
  }
  return undefined;
}

/**
 * Get available ports for a model (returns just port names)
 */
export function getAvailablePorts(modelName: string): string[] {
  const model = getModelByName(modelName);
  return model?.ports.map(p => p.name) || [];
}

/**
 * Get LAN-type ports only (excludes WAN, MGMT, HA, etc.)
 */
export function getLanPorts(modelName: string): string[] {
  const model = getModelByName(modelName);
  if (!model) return [];
  return model.ports
    .filter(p => p.type === 'lan' || p.type === 'sfp' || p.type === 'combo')
    .map(p => p.name);
}

/**
 * Get WAN-type ports only
 */
export function getWanPorts(modelName: string): string[] {
  const model = getModelByName(modelName);
  if (!model) return [];
  return model.ports
    .filter(p => p.type === 'wan')
    .map(p => p.name);
}

/**
 * Get default WAN port for a model
 */
export function getDefaultWanPort(modelName: string): string {
  const model = getModelByName(modelName);
  return model?.defaultWanPort || '';
}

/**
 * Get default LAN port for a model
 */
export function getDefaultLanPort(modelName: string): string {
  const model = getModelByName(modelName);
  return model?.defaultLanPort || '';
}

/**
 * Get default management port (if dedicated)
 */
export function getDefaultMgmtPort(modelName: string): string | undefined {
  const model = getModelByName(modelName);
  return model?.defaultMgmtPort;
}

/**
 * Check if model has hardware switch
 */
export function hasHardwareSwitch(modelName: string): boolean {
  const model = getModelByName(modelName);
  return model?.hasHardwareSwitch || false;
}

/**
 * Get hardware switch name
 */
export function getHardwareSwitchName(modelName: string): string | undefined {
  const model = getModelByName(modelName);
  return model?.hardwareSwitchName;
}

/**
 * Get CLI type for a model
 */
export function getCliType(modelName: string): string {
  const vendor = getVendorForModel(modelName);
  return vendor?.cliType || 'fortios';
}

/**
 * Validate if a port exists on a model
 */
export function isValidPort(modelName: string, portName: string): boolean {
  const ports = getAvailablePorts(modelName);
  return ports.includes(portName);
}

export default HARDWARE_VENDORS;
